import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  useAuthContext,
  AuthProvider as AsgardeoAuthProvider,
} from '@asgardeo/auth-react';
import { authAPI, creditsAPI } from 'services/transactionService';
import wso2Config from "configs/wso2";

export const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const {
    state,
    getBasicUserInfo,
    getIDToken,
    getDecodedIDToken,
    getAccessToken,
    signIn,
    signOut,
  } = useAuthContext();

  const [derivedState, setDerivedState] = useState(null);
  const [localUserData, setLocalUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLogoutFailure, setHasLogoutFailure] = useState(false);
  const [userBalance, setUserBalance] = useState(null);
  const hasInitialized = useRef(false);

  const updateUserBalance = async () => {
    try {
      console.log('UserContext - Calling creditsAPI.getBalance()');
      const response = await creditsAPI.getBalance();
      const newBalance = response.data.credit_balance;
      setUserBalance(newBalance);
      return { success: true, balance: newBalance };
    } catch (error) {
      console.error("Failed to update user balance:", error);
      return { success: false, error: error.message };
    }
  };

  // Handle initial loading state
  useEffect(() => {
    // Set loading to true when authentication state changes
    if (state.isAuthenticated !== undefined) {
      if (state.isAuthenticated) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    }
  }, [state.isAuthenticated]);

  // Initialize loading state properly
  useEffect(() => {
    // Keep loading true until we have a definitive authentication state
    if (state.isAuthenticated === undefined) {
      setLoading(true);
    }
  }, [state.isAuthenticated]);

  useEffect(() => {
    const fetchUserData = async () => {
      
      // Prevent multiple calls during initialization
      if (hasInitialized.current) {
        setLoading(false); // Ensure loading is false even when skipping
        return;
      }
      
      if (typeof window !== "undefined" && state.isAuthenticated) {
        try {
          hasInitialized.current = true;
          const userInfo = await getBasicUserInfo();
          
          const idToken = await getIDToken();
          const accessToken = await getAccessToken();
          const decodedToken = await getDecodedIDToken();

          // Share tokens with api.js
          authAPI.setIdToken(idToken);
          authAPI.setAccessToken(accessToken);

          // Fetch balance immediately after token is set
          await updateUserBalance();

          // Call /me endpoint only once
          if (idToken) {
            console.log("🔍 Calling /me endpoint...");
            try {
              const response = await authAPI.getWSO2UserInfo();
              setLocalUserData(response.data);
            } catch (meError) {
              setError("Failed to get user data from backend");
            }
          } else {
            setError("No authentication token available");
          }

          const role = decodedToken.roles || null;
          setDerivedState({
            authenticateResponse: userInfo,
            idToken: idToken ? idToken.split(".") : null,
            decodedIdTokenHeader: idToken
              ? JSON.parse(atob(idToken.split(".")[0]))
              : null,
            decodedIDTokenPayload: decodedToken,
            role,
          });
          
        } catch (fetchError) {
          setError("Failed to fetch user data");
        }
      } else if (typeof window !== "undefined" && state.isAuthenticated === false) {
        setDerivedState(null);
        setLocalUserData(null);
        hasInitialized.current = false;
      }
      
      setLoading(false);
    };

    if (state.isAuthenticated !== undefined) {
      fetchUserData();
    }
  }, [state.isAuthenticated, getBasicUserInfo, getIDToken, getAccessToken, getDecodedIDToken]);

  // Reset balance on logout
  useEffect(() => {
    if (state.isAuthenticated === false) {
      setUserBalance(null);
    }
  }, [state.isAuthenticated]);

  const login = async () => {
    setError(null);
    setHasLogoutFailure(false);
    try {
      await signIn();
    } catch {
      setError("Login failed");
    }
  };

  const logout = async () => { 
    setError(null);
    try {
      await authAPI.logout(); // Call backend logout to clear cache
    } catch (error) {
      console.error("Backend logout failed:", error);
    } finally {
      authAPI.clearTokens(); // Clear shared tokens
      signOut(); // Sign out from WSO2/Asgardeo
    }
  };

  // Expose logout function to window for API interceptor
  React.useEffect(() => {
    window.logoutUser = logout;
    window.updateUserBalance = updateUserBalance;
    return () => {
      delete window.logoutUser;
      delete window.updateUserBalance;
    };
  }, [logout, updateUserBalance]);

  const syncProfile = async () => {
    try {
      const response = await authAPI.syncWSO2Profile();
      setLocalUserData(response.data.user);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Failed to sync profile:", error);
      return { success: false, error: error.message };
    }
  };

  return (
    <UserContext.Provider
      value={{
        derivedState,
        localUserData,
        setLocalUserData,
        loading,
        error,
        hasLogoutFailure,
        userBalance,
        updateUserBalance,
        login,
        logout,
        syncProfile,
        isAuthenticated: state.isAuthenticated,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const UserProviderWrapper = ({ children }) => (
  <AsgardeoAuthProvider config={wso2Config}>
    <UserProvider>{children}</UserProvider>
  </AsgardeoAuthProvider>
); 
